import { useDraggable } from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { Badge } from "design-system/atoms/Badge"
import { Card, CardHeader, CardTitle } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { GripVertical } from "lucide-react"
import type { CSSProperties } from "react"
import { useTranslation } from "react-i18next"
import { ClusterSnapshotDataSchema } from "schema"
import z from "zod"

type ClusterSnapshot = z.infer<typeof ClusterSnapshotDataSchema>

export const ClusterSnapshotItem = ({ snapshot }: { snapshot: ClusterSnapshot }) => {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: snapshot.id,
    data: { type: "SNAPSHOT", snapshot },
  })

  const style: CSSProperties = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    transition: isDragging ? undefined : "transform 200ms ease, box-shadow 200ms ease",
    boxShadow: isDragging ? "0 10px 30px rgba(15, 23, 42, 0.25)" : undefined,
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        "shadow-sm transition-shadow duration-200",
        isDragging && "opacity-80 relative z-50",
      )}
    >
      <CardHeader className="flex flex-row items-start gap-2 p-2">
        <div
          {...listeners}
          {...attributes}
          className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div className="flex justify-between items-center gap-4 w-full">
          <CardTitle className="text-base font-medium leading-none">{snapshot.label}</CardTitle>
          <div className="flex flex-row gap-2 items-center min-w-fit">
            <Badge variant="secondary">
              {t("backoffice.canonicalTopics.itemsCount", { count: snapshot.itemsCount })}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {new Date(snapshot.from).toLocaleDateString()}
            </span>
          </div>
        </div>
      </CardHeader>
    </Card>
  )
}
